import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { PRM } from '@reflact/prmacweb';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { UploadState, UploadxDirective, UploadxOptions, UploadxService } from 'ngx-uploadx';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { VideoService } from 'src/app/shared/video/video.service';

@Component({
  selector: 'app-moduploadvideo',
  templateUrl: './moduploadvideo.component.html',
  styleUrls: ['./moduploadvideo.component.css'],
  imports: [
    CommonModule,
    UploadxDirective
  ],
  standalone: true
})
export class ModuploadvideoComponent implements OnInit, OnDestroy {
  @Input() public participant!: PRM.IpadParticipant;
  @Input() public ev!: PRM.Event;
  @Input() public pack!: PRM.IpadPackage;

  public modalRef?: BsModalRef;
  public videoid: string = "AAA"
  public uploadProgress: number;
  public uploadStatus: string;
  public isUploading = false;
  public videoSrc = '';
  private loadedVideoId: string | null = null;
  public selected: boolean = false;
  public fileName = '';
  /** Name der zuletzt für diese Komponente ausgewählten Datei (zur Identifikation des eigenen Uploads). */
  private pendingFileName: string | null = null;
  /** Upload-ID des eigenen Uploads (wird beim 'added'-Event ermittelt). */
  private myUploadId: string | null = null;

  public uploadxOptions: UploadxOptions = {
    allowedTypes: 'video/mp4',
    concurrency: 2,
    endpoint: '/api/upload/participantvideoasobserver',
    metadata: {
      participantid: "",
      videotype: "selfRecording",
      timeblockid: ""
    }
  };

  constructor(private authService: AuthService, private http: HttpClient, public videoService: VideoService, private modalService: BsModalService, public uploadService: UploadxService, private toastr: ToastrService) { }

  ngOnInit(): void {
    this.uploadxOptions.metadata = {
      participantid: this.participant.id,
      videotype: 'selfRecording',
      timeblockid: this.ev.tbid
    };
    this.uploadxOptions.headers = (): Record<string, string> => {
      const webAppToken = this.authService.getWebAppToken();
      return { webapptoken: webAppToken || '' };
    };
    this.updateExistingVideoFile()
  }

  ngOnDestroy(): void {
    this.releaseVideoPreview();
  }

  private releaseVideoPreview(): void {
    if (this.videoSrc) {
      this.videoService.releaseVideoUrl(this.videoSrc);
      this.videoSrc = '';
      this.loadedVideoId = null;
    }
  }

  public async deleteVideo() {

    await firstValueFrom(this.http.delete("/api/participantvideo/" + this.videoid));

    this.modalRef.hide()
    this.updateExistingVideoFile();
  }

  public async updateExistingVideoFile() {

    const existingVideo: any = await firstValueFrom(this.http.get("/api/participantvideoasobserver/" + this.ev.tbid + "/" + this.participant.id));
    if (existingVideo != null) {
      this.videoid = existingVideo._id
      if (this.loadedVideoId !== this.videoid) {
        this.releaseVideoPreview();
        try {
          this.videoSrc = await this.videoService.playVideo(this.videoid);
          this.loadedVideoId = this.videoid;
        } catch (error) {
          console.error('Unable to load observer video preview', error);
          this.toastr.error($localize`:@@videoLoadFailed:Das Video konnte nicht geladen werden.`);
        }
      }
    } else {
      this.videoid = ""
      this.releaseVideoPreview();
    }
  }

  public onState(e) { }

  /**
   * Wird vom (change)-Event des Datei-Inputs aufgerufen, BEVOR die [uploadx]-Direktive
   * die Datei an den Service übergibt. Wir merken uns den Dateinamen, um beim
   * darauffolgenden 'added'-State-Event die zu uns gehörende uploadId zu identifizieren.
   */
  public onFileSelected(event: Event) {
    const files = (event.target as HTMLInputElement).files;
    if (files && files.length > 0) {
      this.pendingFileName = files[0].name;
      this.myUploadId = null;
      this.uploadProgress = 0;
    }
  }

  public onStateChanged(newState: UploadState) {
    // Der UploadxService ist ein Singleton; das (state)-Output der Direktive relayt
    // den globalen Event-Stream. Wir müssen daher selbst filtern, damit der Fortschritt
    // nur in der Komponente angezeigt wird, deren Datei tatsächlich hochgeladen wird.
    if (this.myUploadId === null) {
      // Eigene uploadId beim 'added'-Event über den gemerkten Dateinamen ermitteln.
      if (
        newState.status === 'added' &&
        this.pendingFileName !== null &&
        newState.name === this.pendingFileName
      ) {
        this.myUploadId = newState.uploadId;
        this.pendingFileName = null;
      } else {
        return;
      }
    }

    if (newState.uploadId !== this.myUploadId) {
      return;
    }

    this.uploadProgress = newState.progress || 0;
    this.uploadStatus = newState.status;
    if (newState.status === "uploading") {
      this.isUploading = true;
    }
    if (newState.status === "complete") {
      this.isUploading = false;
      this.myUploadId = null;
      this.updateExistingVideoFile();
    }
    if (newState.status === "cancelled" || newState.status === "error") {
      this.isUploading = false;
      this.myUploadId = null;
    }
  }

  public openModal(template: TemplateRef<any>) {
    this.modalRef = this.modalService.show(template, { class: 'modal-lg' });
  }
}